import { Chip } from '@mui/material';
import { alpha, useTheme, Theme } from '@mui/material/styles';
import { css } from '@emotion/css';
import Table from '@components/TableComponents/Table';
import { Mobility_Vehicles } from '@gql/base-types';
import { BiPencil } from 'react-icons/bi';

/** Column config carrier — Table harvests these props from its children. */
interface ColumnProps {
  name: string;
  value?: (data: any) => any;
  label: string;
  style?: object;
  align?: 'left' | 'center' | 'right';
  disableSort?: boolean;
}
const Column: React.FC<ColumnProps> = () => null;

const defaultOrder = `desc`;
const defaultOrderBy = `updated_at`;

interface MobilityVehiclesTableProps {
  vehicles: Mobility_Vehicles[];
  onEdit: (vehicle: Mobility_Vehicles) => void;
}

const STATUS_LABEL: Record<string, string> = {
  available: 'Available',
  in_use: 'In Use',
  maintenance: 'Maintenance',
  retired: 'Retired',
};

// HopDrive design system: status chips are a light background that's a tint of the
// (darker) text color — same hue, no border. The fill is derived from the color's
// .main via alpha() rather than a `.50` palette path, which doesn't resolve on the
// dealer-portal theme. Connotation: available=green, in use=blue, maintenance=amber.
const statusStyle = (theme: Theme, status: string): { color: string; bg: string } => {
  const swatch = (main: string, text?: string) => ({ color: text ?? main, bg: alpha(main, 0.14) });
  switch (status) {
    case 'available':
      return swatch(theme.palette.success.main, theme.palette.success.dark);
    case 'in_use': {
      // secondaryAccent is a custom HopDrive palette key, not in MUI's Palette type.
      const blue = (theme.palette as any).secondaryAccent?.main ?? '#4068a0';
      return swatch(blue);
    }
    case 'maintenance':
      return swatch(theme.palette.warning.main, theme.palette.warning.dark);
    case 'retired':
    default:
      return { color: theme.palette.grey[700], bg: alpha(theme.palette.grey[600], 0.14) };
  }
};

const vehicleName = (v: Mobility_Vehicles) =>
  [v?.year, v?.make, v?.model].filter(Boolean).join(' ').trim() || `Vehicle ${v?.id}`;

const MobilityVehiclesTable: React.FC<MobilityVehiclesTableProps> = ({ vehicles, onEdit }) => {
  const theme = useTheme();
  const cls = useStyles();

  const rowActions = [
    {
      name: 'edit-vehicle',
      label: 'Edit',
      icon: <BiPencil className={cls.menuIcon} />,
      handler: (vehicle: Mobility_Vehicles) => onEdit(vehicle),
    },
  ];

  return (
    <Table
      data={vehicles}
      defaultOrderBy={defaultOrderBy}
      defaultOrder={defaultOrder}
      tableAriaLabel='mobility-vehicles'
      stickyHeader={true}
      rowActions={rowActions}
    >
      <Column
        name='make'
        value={(v: Mobility_Vehicles) => vehicleName(v)}
        label='Vehicle'
        style={{ minWidth: 160 }}
      />
      <Column
        name='license_plate'
        value={(v: Mobility_Vehicles) => v?.license_plate || '-'}
        label='Plate'
        style={{ minWidth: 100 }}
      />
      <Column
        name='vin'
        value={(v: Mobility_Vehicles) =>
          v?.vin ? (
            <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 13 }}>{v.vin}</span>
          ) : (
            '-'
          )
        }
        label='VIN'
        style={{ minWidth: 140 }}
      />
      <Column name='color' value={(v: Mobility_Vehicles) => v?.color || '-'} label='Color' style={{ minWidth: 90 }} />
      <Column
        name='capacity'
        value={(v: Mobility_Vehicles) => (v?.capacity != null ? `${v.capacity}` : '-')}
        label='Capacity'
        align='center'
        style={{ minWidth: 90 }}
      />
      <Column
        name='status'
        value={(v: Mobility_Vehicles) => {
          const s = statusStyle(theme, v?.status);
          return (
            <Chip
              size='small'
              label={STATUS_LABEL[v?.status] ?? v?.status ?? '-'}
              sx={{
                bgcolor: s.bg,
                color: s.color,
                fontWeight: 600,
                fontSize: 12,
                height: 24,
                borderRadius: '6px', // radius.sm — DS chip radius
                '& .MuiChip-label': { px: 1 },
              }}
            />
          );
        }}
        label='Status'
        align='center'
        style={{ minWidth: 120 }}
      />
    </Table>
  );
};

const useStyles = () => ({
  menuIcon: css`
    display: block;
    margin-top: -1px;
    margin-right: 8px;
  `,
});

export default MobilityVehiclesTable;
